// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';

// material ui
import {
  Box,
  Card,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from '@mui/material';

// feature-utils
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';

// components
// utils
import { getTypeColor } from '@/utils/nodeTypeMapper';

interface EnergyTreeNode {
  energyTreeNodeId: string | number;
  type: string;
  name?: string;
  EnergyTreeNodeId?: number;
}

interface EnergyGroup {
  // Add specific properties as needed
  [key: string]: unknown;
}

interface NodeInitiativeInclusionListProps {
  clickedNode: EnergyTreeNode;
  energyGroup: EnergyGroup;
}

const NodeInitiativeInclusionList = ({
  clickedNode,
  energyGroup,
}: NodeInitiativeInclusionListProps) => {
  const { t } = useTranslation('energyxplorer');

  if (!clickedNode || !energyGroup) return null;

  const inclusions = getInitiativeInclusionsForNode(
    clickedNode.energyTreeNodeId,
    energyGroup,
  );

  if (!inclusions.length) return null;

  return (
    <Card variant="outlined">
      <List
        dense
        subheader={
          <ListSubheader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EnergySavingsLeafRoundedIcon fontSize="small" />
              {t('nodes.initiative_list.included_in_initiatives')}
              <Chip size="small" label={inclusions.length} />
            </Box>
          </ListSubheader>
        }
      >
        <Divider />
        {inclusions.map(({ initiative, isDirect, viaNode }, index) => {
          const key =
            initiative.energyInitiativeId ||
            initiative.id ||
            initiative.guid ||
            index;
          const label =
            initiative.name ||
            initiative.title ||
            `${t('nodes.initiative_list.initiative_prefix')}${initiative.energyInitiativeId || initiative.id || ''}`.trim();

          return (
            <ListItem key={key}>
              <ListItemText
                primary={label}
                secondary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isDirect && (
                      <Typography variant="caption" color="text.secondary">
                        {t('nodes.initiative_list.direct_use')}
                      </Typography>
                    )}

                    {!isDirect && viaNode && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {t('nodes.initiative_list.indirect_via')}
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            viaNode.name ??
                            `${t('nodes.initiative_list.node_prefix')}${viaNode.energyTreeNodeId || viaNode.EnergyTreeNodeId}`
                          }
                          sx={{
                            backgroundColor: getTypeColor(viaNode.type),
                            color: '#fff',
                          }}
                        />
                      </Stack>
                    )}
                  </Stack>
                }
                slotProps={{
                  secondary: { component: 'span' }
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Card>
  );
};

export default NodeInitiativeInclusionList;
